import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Image } from '@thd-olt-component-react/core-ui';
import { useNavigate } from '@thd-olt-component-react/router';
import { useConfigService } from '@thd-nucleus/experience-context';
import { ProfileImageWithFavoriteIcon } from '../../../../components/common/ProfileImageWithFavoriteIcon/ProfileImageWithFavoriteIcon';
import JobApplicantActionDropdown from '../../../../components/ActionDropdowns/JobApplicantActionDropdown';
import { useStore } from '../../../../store';
import {
  APPLICANT_STATUS_JSX_MAP,
  EXP_ROUTES
} from '../../../../utils/constants';
import MobileDashboardSorting from '../../../../components/common/MobileDashboardSorting/MobileDashboardSorting';
import { splitExperienceString } from '../../../../components/common/DashboardTableExperienceLevel/DashboardTableExperienceLevel';
import { truncateName } from '../../../../utils/formatters';
import LocationView from '../../../../components/common/LocationView/LocationView';
import { generateImageSource } from '../../../../utils/generateImageSource';

const MobileJobApplicantsListItem = ({ applicant }) => {
  const experience = splitExperienceString(applicant.yearsOfExperience);

  return (
    <>
      <div
        className="p2p-job-applicants__list-item"
      >
        <div
          className="p2p-job-applicants__list-item-row"
        >
          <div>
            <ProfileImageWithFavoriteIcon candidate={applicant} />
            <div className="p2p-job-applicants__list-item-status">
              {APPLICANT_STATUS_JSX_MAP[applicant.status]}
            </div>
          </div>
          <div>
            <div className="p2p-job-applicants__list-item-title">
              {truncateName(applicant.name)}
            </div>
            <div>
              {applicant.tradeTypes && applicant.tradeTypes.join(', ')}
            </div>
            <div style={{ fontWeight: 'bold' }}>
              {experience[0]}, {experience[1]}
            </div>
            <div className="p2p-job-applicants__list-item-location">
              <LocationView zipCode={applicant?.zipCode} city={applicant?.city} state={applicant?.state} />
            </div>
          </div>
        </div>
        <JobApplicantActionDropdown
          applicant={applicant}
        />
      </div>
    </>
  );
};

const _MobileJobApplicants = () => {

  const isHiredEnabled = useConfigService('fs:isHiredEnabled');

  const {
    jobApplicantStore: {
      jobApplicants,
      jobPostingMetadata,
      setFilter,
      filter,
      setSort,
      sort,
      setPage
    }
  } = useStore();

  const navigate = useNavigate();

  const sortOptions = [
    { headerText: 'Name: A to Z', sort: { by: 'name', order: 'asc' } },
    { headerText: 'Name: Z to A', sort: { by: 'name', order: 'desc' } },
    { headerText: 'Location: A to Z', sort: { by: 'location', order: 'asc' } },
    { headerText: 'Location: Z to A', sort: { by: 'location', order: 'desc' } },
    { headerText: 'Status: A to Z', sort: { by: 'status', order: 'asc' } },
    { headerText: 'Status: Z to A', sort: { by: 'status', order: 'desc' } }
  ];

  const filterOptions = [
    { headerText: 'Show All', sort: 'showAll', show: true },
    { headerText: 'Invited', sort: 'invited', show: true },
    { headerText: 'Applied', sort: 'applied', show: true },
    { headerText: 'Hired', sort: 'hired', show: isHiredEnabled },
    { headerText: 'Not Hired', sort: 'not_hired', show: isHiredEnabled },
    { headerText: 'Declined', sort: 'declined', show: true }
  ];

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1);
  };
  const renderJobApplicantSubTitle = () => {
    return (
      <div className="ptp-job-posting__sub-title">
        <Image
          src={generateImageSource('/images/v1/path-to-pro/job-applicants-icon.svg')}
          className="ptp-job-posting__job-applicants-icon"
        />
          Job Applicants
      </div>
    );
  };

  const renderJobApplicantSubText = () => {

    if (jobApplicants?.length > 0) {
      return (
        <div className="my-dashboard__section-text">
            The following list of people have been invited or applied to your job posting:
          <span className="job-applicants__describtion-job-name">{jobPostingMetadata.title || ''}</span>.<br />
            To view their detail, simply click on the applicant. You can also manage the pool using the Action column far right.
        </div>
      );
    }
    return (
      <div className="my-dashboard__section-text">
        You don’t have any job applicants yet. While you’re waiting for candidates to apply, try browsing our
        pool of qualified skilled trades people.
        You can call or email any candidate, even if they haven’t applied.
        <Button style={{ marginTop: '13px' }} data-testid="find-candidates" onClick={() => { navigate(EXP_ROUTES.CANDIDATE_SEARCH); }}>
          Find Candidates
        </Button>
      </div>
    );

  };

  const renderSortingBlock = () => {

    return (
      <div className="my-dashboard__sorting-block">
        <MobileDashboardSorting
          placeholder="Sort Candidates"
          testId="job-applicants-mobile-sort-dropdown"
          options={sortOptions}
          setSort={setSort}
          sort={sort}
        />
      </div>
    );
  };

  const renderFilterBlock = () => {

    return (
      <div className="my-dashboard__sorting-block">
        <MobileDashboardSorting
          placeholder="Filter Candidates"
          testId="job-applicants-mobile-filter-dropdown"
          headerClass="ptp-job-posting__mobile-dropdown-header"
          options={filterOptions.filter((opt) => opt.show)}
          setSort={handleFilterChange}
          sort={filter}
        />
      </div>
    );
  };

  const renderData = () => {
    return (
      <div className="p2p-job-applicants__items-list">
        {jobApplicants.map((row, index) => (
          <MobileJobApplicantsListItem applicant={row} key={index} />
        ))}
      </div>
    );
  };

  const renderJobApplicantsSection = () => {
    return (
      <>
        <div style={{ padding: '10px 10px 20px' }}>
          {renderJobApplicantSubTitle()}
          {renderJobApplicantSubText()}
        </div>
        {renderFilterBlock()}
        {jobApplicants?.length > 0 && renderSortingBlock()}
        {renderData()}
      </>
    );
  };

  return (
    <>
      {renderJobApplicantsSection()}
    </>
  );
};

const MobileJobApplicants = observer(_MobileJobApplicants);

export default MobileJobApplicants;
